import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  CalendarIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  AtSymbolIcon,
  ArrowRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge } from '@/components/ui'
import { dashboardApi } from '@/api'
import type { DashboardNotification } from '@/api/dashboard'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { it } from 'date-fns/locale'

const notificationIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationTriangleIcon,
  mention: AtSymbolIcon,
  reply: ChatBubbleLeftRightIcon,
  badge: TrophyIcon,
  course: AcademicCapIcon,
  event: CalendarIcon,
  taskforce: UserGroupIcon,
  clinic: InformationCircleIcon,
}

const notificationColors: Record<string, { bg: string; icon: string; badge: 'info' | 'success' | 'warning' | 'danger' }> = {
  info: { bg: 'bg-blue-50', icon: 'text-blue-500', badge: 'info' },
  success: { bg: 'bg-green-50', icon: 'text-green-500', badge: 'success' },
  warning: { bg: 'bg-yellow-50', icon: 'text-yellow-500', badge: 'warning' },
  error: { bg: 'bg-red-50', icon: 'text-red-500', badge: 'danger' },
  mention: { bg: 'bg-purple-50', icon: 'text-purple-500', badge: 'info' },
  reply: { bg: 'bg-indigo-50', icon: 'text-indigo-500', badge: 'info' },
  badge: { bg: 'bg-yellow-50', icon: 'text-yellow-600', badge: 'warning' },
  course: { bg: 'bg-blue-50', icon: 'text-blue-600', badge: 'info' },
  event: { bg: 'bg-purple-50', icon: 'text-purple-600', badge: 'info' },
  taskforce: { bg: 'bg-orange-50', icon: 'text-orange-600', badge: 'warning' },
  clinic: { bg: 'bg-green-50', icon: 'text-green-600', badge: 'success' },
}

interface Props {
  notifications?: DashboardNotification[]
  unreadCount?: number
}

export default function NotificationsWidget({ notifications: initialNotifications, unreadCount: initialUnread }: Props) {
  const [notifications, setNotifications] = useState<DashboardNotification[]>(initialNotifications || [])
  const [unreadCount, setUnreadCount] = useState(initialUnread || 0)
  const [loading, setLoading] = useState(!initialNotifications)

  useEffect(() => {
    if (!initialNotifications) {
      fetchNotifications()
    }
  }, [initialNotifications])

  const fetchNotifications = async () => {
    try {
      const data = await dashboardApi.getNotifications({ page: 1 })
      setNotifications(data.results.slice(0, 5))
      setUnreadCount(data.results.filter(n => !n.is_read).length)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await dashboardApi.markAllNotificationsRead()
      setNotifications(notifications.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking notifications as read:', error)
    }
  }

  const handleMarkRead = async (id: number) => {
    try {
      await dashboardApi.markNotificationRead(id)
      setNotifications(notifications.map(n =>
        n.id === id ? { ...n, is_read: true } : n
      ))
      setUnreadCount(Math.max(0, unreadCount - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="Notifiche" />
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-gray-500" />
            <span>Notifiche</span>
            {unreadCount > 0 && (
              <Badge variant="danger" size="sm">{unreadCount}</Badge>
            )}
          </div>
        }
        action={
          unreadCount > 0 ? (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <CheckIcon className="h-3 w-3" />
              Segna tutte lette
            </button>
          ) : null
        }
      />

      {notifications.length > 0 ? (
        <div className="space-y-2">
          {notifications.map((notification) => {
            const Icon = notificationIcons[notification.type] || InformationCircleIcon
            const colors = notificationColors[notification.type] || notificationColors.info

            return (
              <div
                key={notification.id}
                className={`
                  p-3 rounded-lg border transition-all cursor-pointer
                  ${notification.is_read
                    ? 'bg-gray-50 border-gray-100 opacity-70'
                    : `${colors.bg} border-gray-200`
                  }
                  hover:shadow-sm
                `}
                onClick={() => !notification.is_read && handleMarkRead(notification.id)}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-full ${colors.bg}`}>
                    <Icon className={`h-4 w-4 ${colors.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${notification.is_read ? 'text-gray-500' : 'text-gray-900'}`}>
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <span className="flex-shrink-0 h-2 w-2 bg-primary-500 rounded-full" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistanceToNow(parseISO(notification.created_at), {
                        addSuffix: true,
                        locale: it
                      })}
                    </p>
                  </div>
                  {notification.link && (
                    <Link
                      to={notification.link}
                      className="flex-shrink-0 text-primary-600 hover:text-primary-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <BellIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">
            Nessuna notifica
          </p>
        </div>
      )}

      <div className="mt-4 pt-3 border-t">
        <Link
          to="/notifications"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center gap-1"
        >
          Vedi tutte le notifiche
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>
    </Card>
  )
}
